import React, { useEffect, useState } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AlertCircle, Loader2 } from 'lucide-react';
import axios from '../api/client';
import { API } from '../api/config';
import { stripeElementStyle } from '../stripeElementStyle';
import { useAuth } from '../contexts/AuthContext';
import { ShippingAddress } from './ShippingAddressBook';

// Scenario 1-A (BDD line 41: 「選擇單個或多個商品合併打包」) — pay one shared
// shipping fee for a package that combines 2+ of the customer's own captured
// orders. Mirrors ShippingFeeModal.tsx's single-order flow almost exactly
// (todo.txt 15) — kept as a separate component so the already-tested
// single-order path stays untouched.

const stripePk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx';
const stripePromise = loadStripe(stripePk);

type ShippingPaymentMethod = 'card' | 'bank_transfer' | 'reward';

const errorBanner = (message: string) => (
  <div style={{ color: '#991B1B', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
    <AlertCircle size={14} /> {message}
  </div>
);

const CardPackageShippingFeeForm: React.FC<{
  packageId: string;
  amountDue: number;
  onPaid: () => void;
}> = ({ packageId, amountDue, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setCardError(null);

    try {
      const intentRes = await axios.post(
        API.packageShippingPaymentIntent(packageId),
        { payment_method: 'card' },
        { headers: authHeaders() },
      );
      const { client_secret } = intentRes.data;

      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) { setCardError('Card element not found. Please refresh and try again.'); setProcessing(false); return; }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: cardNumber },
      });

      if (confirmError) {
        setCardError(confirmError.message || 'Payment failed. Please check your card details.');
        setProcessing(false);
        return;
      }
      if (paymentIntent?.status !== 'succeeded') {
        setCardError('Unexpected payment status: ' + paymentIntent?.status + '. Please contact support.');
        setProcessing(false);
        return;
      }

      await axios.post(API.confirmPackageShippingPayment(packageId), {}, { headers: authHeaders() });
      onPaid();
    } catch (err: any) {
      setCardError(err.response?.data?.detail || err.message || 'Payment error. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <div className="form-group">
        <label className="form-label">Card Number</label>
        <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
          <CardNumberElement options={{ style: stripeElementStyle }} onChange={(e) => { setCardComplete(e.complete); setCardError(e.error ? e.error.message : null); }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div>
          <label className="form-label">Expiry Date</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardExpiryElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
        <div>
          <label className="form-label">CVC</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardCvcElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
      </div>
      {cardError && errorBanner(cardError)}
      <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={!stripe || !cardComplete || processing}>
        {processing ? <><Loader2 className="animate-spin" size={16} /> Processing...</> : `Pay £${amountDue.toFixed(2)}`}
      </button>
    </form>
  );
};

// Same UK-domestic detection used elsewhere (QuickQuote.tsx, ShippingFeeModal.tsx).
const isUkShippingAddress = (country: string) => {
  const normalized = country.trim().toLowerCase();
  return ['england', 'scotland', 'wales', 'northern ireland', 'isle of man', 'uk', 'united kingdom', 'gb', 'great britain'].includes(normalized);
};

const PackageShippingModal: React.FC<{ packageId: string; orderCount: number; onClose: () => void; onDone: () => void }> = ({ packageId, orderCount, onClose, onDone }) => {
  const { user } = useAuth();
  const [savedAddresses, setSavedAddresses] = useState<ShippingAddress[] | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [addressSaved, setAddressSaved] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [adminBaseFee, setAdminBaseFee] = useState<number | null | undefined>(undefined);
  const [checkingAdminFee, setCheckingAdminFee] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<ShippingPaymentMethod>('card');
  const [quote, setQuote] = useState<{ amount_due: number } | null>(null);
  const [rewardBalance, setRewardBalance] = useState<number | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<ShippingPaymentMethod | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => {
    axios.get(API.getRewardBalance(), { headers: authHeaders() })
      .then((res) => setRewardBalance(res.data.balance))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(API.listShippingAddresses(user.id), { headers: authHeaders() })
      .then((res) => setSavedAddresses(res.data))
      .catch(() => setSavedAddresses([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const showUkWarning = isUkShippingAddress(destinationCountry);

  const saveAddress = async () => {
    const selected = savedAddresses?.find((a) => a.id === selectedAddressId);
    if (!selected) return;
    setSavingAddress(true);
    setAddressError('');
    try {
      await axios.post(
        API.setPackageShippingAddress(packageId),
        { country: selected.country, address: { full_address: selected.address } },
        { headers: authHeaders() },
      );
      setDestinationCountry(selected.country);
      setAddressSaved(true);
      await checkAdminFee();
    } catch (err: any) {
      setAddressError(err.response?.data?.detail || 'Failed to save the delivery address.');
    } finally {
      setSavingAddress(false);
    }
  };

  // BDD Scenario 1-A: Admin packs the combined package and enters the real
  // carrier cost — the customer no longer types a shipping fee themselves.
  const checkAdminFee = async () => {
    setCheckingAdminFee(true);
    setError('');
    try {
      const res = await axios.get(API.getPackage(packageId), { headers: authHeaders() });
      setAdminBaseFee(res.data.admin_base_shipping_fee ?? null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to check the shipping fee status.');
    } finally {
      setCheckingAdminFee(false);
    }
  };

  const getQuote = async () => {
    setQuoting(true);
    setError('');
    try {
      const res = await axios.post(
        API.packageShippingQuote(packageId),
        { payment_method: paymentMethod },
        { headers: authHeaders() },
      );
      setQuote(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to get a shipping quote.');
    } finally {
      setQuoting(false);
    }
  };

  const payWithReward = async () => {
    setPaying(true);
    setError('');
    try {
      await axios.post(
        API.packageShippingPaymentIntent(packageId),
        { payment_method: 'reward' },
        { headers: authHeaders() },
      );
      await axios.post(API.confirmPackageShippingPayment(packageId), {}, { headers: authHeaders() });
      setDone('reward');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to pay with reward balance.');
    } finally {
      setPaying(false);
    }
  };

  const payWithBankTransfer = async () => {
    setPaying(true);
    setError('');
    try {
      await axios.post(
        API.packageShippingPaymentIntent(packageId),
        { payment_method: 'bank_transfer' },
        { headers: authHeaders() },
      );
      setDone('bank_transfer');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start bank transfer.');
    } finally {
      setPaying(false);
    }
  };

  const insufficientReward = rewardBalance !== null && quote !== null && rewardBalance < quote.amount_due;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000 }}>
      <div className="section-card" style={{ width: '100%', maxWidth: '460px', padding: '28px' }}>
        <h2 className="card-title" style={{ marginBottom: '8px' }}>Combined Shipment ({orderCount} order{orderCount > 1 ? 's' : ''})</h2>

        {done ? (
          <div>
            <p style={{ color: '#166534', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '12px 16px', fontSize: '14px', marginBottom: '20px' }}>
              {done === 'bank_transfer'
                ? "We've recorded your shipping fee. Our team will confirm it once your bank transfer arrives."
                : 'Shipping fee paid for all combined orders.'}
            </p>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={onDone}>Done</button>
          </div>
        ) : !addressSaved ? (
          <>
            <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '20px' }}>
              Where should we ship this combined package? UK-domestic
              destinations don't qualify for the export-advantaged Bonus
              Reward.
            </p>
            {savedAddresses && savedAddresses.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px' }}>
                You don't have any saved addresses yet.{' '}
                <a href="/settings/addresses" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-moss)' }}>
                  Add one in Settings
                </a>, then come back here.
              </p>
            ) : (
              <div className="form-group">
                <label className="form-label" htmlFor="package-shipping-address-select">Destination Address</label>
                <select
                  id="package-shipping-address-select"
                  className="form-input"
                  value={selectedAddressId}
                  onChange={(e) => {
                    setSelectedAddressId(e.target.value);
                    const selected = savedAddresses?.find((a) => a.id === e.target.value);
                    setDestinationCountry(selected?.country || '');
                  }}
                >
                  <option value="">Select a saved address</option>
                  {savedAddresses?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label ? `${a.label} — ` : ''}{a.country} — {a.address}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '12px', color: '#64748B', marginTop: '6px' }}>
                  <a href="/settings/addresses" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-moss)' }}>Manage saved addresses</a>
                </p>
              </div>
            )}
            {showUkWarning && (
              <div style={{ color: '#92400E', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FFFBEB', padding: '10px', borderRadius: '6px' }}>
                <AlertCircle size={14} /> UK domestic shipping doesn't qualify for a Bonus Reward on these orders.
              </div>
            )}
            {addressError && errorBanner(addressError)}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button className="btn btn-outline" onClick={onClose} disabled={savingAddress}>Cancel</button>
              <button className="btn btn-primary" onClick={saveAddress} disabled={savingAddress || !selectedAddressId}>
                {savingAddress ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </>
        ) : adminBaseFee == null ? (
          <>
            <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '20px' }}>
              Our team is packing this combined package and will enter the
              real shipping cost shortly. Check back once you've been
              notified it's ready to pay.
            </p>
            {error && errorBanner(error)}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button className="btn btn-outline" onClick={onClose} disabled={checkingAdminFee}>Cancel</button>
              <button className="btn btn-primary" onClick={checkAdminFee} disabled={checkingAdminFee}>
                {checkingAdminFee ? 'Checking...' : 'Check Again'}
              </button>
            </div>
          </>
        ) : !quote ? (
          <>
            <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '20px' }}>
              Shipping fee: £{adminBaseFee.toFixed(2)}. Card payment adds a 3% processing fee;
              bank transfer and reward balance have no markup.
            </p>
            <div className="form-group">
              <label className="form-label">Payment Method</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {([
                  { value: 'card' as const, label: 'Card' },
                  { value: 'bank_transfer' as const, label: 'Bank Transfer' },
                  { value: 'reward' as const, label: 'Reward Balance' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={paymentMethod === opt.value ? 'btn btn-primary' : 'btn btn-outline'}
                    style={{ flex: '1 1 120px', fontSize: '13px' }}
                    onClick={() => setPaymentMethod(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {error && errorBanner(error)}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button className="btn btn-outline" onClick={onClose} disabled={quoting}>Cancel</button>
              <button className="btn btn-primary" onClick={getQuote} disabled={quoting}>
                {quoting ? 'Getting Quote...' : 'Get Quote'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ background: 'var(--text-charcoal)', color: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '6px' }}>Amount Due ({paymentMethod.replace('_', ' ')})</p>
              <p style={{ fontSize: '28px', fontWeight: 700, color: '#D4AF37' }}>£{quote.amount_due.toFixed(2)}</p>
            </div>

            {error && errorBanner(error)}

            {paymentMethod === 'card' && (
              <Elements stripe={stripePromise}>
                <CardPackageShippingFeeForm packageId={packageId} amountDue={quote.amount_due} onPaid={() => setDone('card')} />
              </Elements>
            )}
            {paymentMethod === 'reward' && (
              <>
                <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '12px' }}>
                  Reward balance: £{(rewardBalance ?? 0).toFixed(2)}
                </p>
                {insufficientReward && errorBanner("Your reward balance doesn't cover this fee.")}
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={payWithReward} disabled={paying || insufficientReward}>
                  {paying ? 'Processing...' : `Pay £${quote.amount_due.toFixed(2)} with Reward Balance`}
                </button>
              </>
            )}
            {paymentMethod === 'bank_transfer' && (
              <>
                <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '12px' }}>
                  We'll send you our bank details. Your shipment is held until our team confirms your transfer has arrived.
                </p>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={payWithBankTransfer} disabled={paying}>
                  {paying ? 'Processing...' : 'Confirm Bank Transfer Payment'}
                </button>
              </>
            )}

            <div style={{ marginTop: '16px' }}>
              <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => setQuote(null)} disabled={paying}>
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PackageShippingModal;
